import dayjsLib from "dayjs";
import utc from "dayjs/plugin/utc";
import "dayjs/locale/ar";
import "dayjs/locale/es";
import "dayjs/locale/id";
import "dayjs/locale/mn";
import "dayjs/locale/pt-br";
import "dayjs/locale/th";
import "dayjs/locale/vi";
import "dayjs/locale/zh-tw";
import "dayjs/locale/zh";
dayjsLib.extend(utc);

export const dayjs: typeof dayjsLib = dayjsLib;
export type Dayjs = dayjsLib.Dayjs;

export enum DateFormats {
  DD_MMM_YYYY = "DD MMM YYYY",
  YYYY_MM_DD = "YYYY MM DD",
  YYYY_M_D_CJK = "YYYY年M月D日",
}

const LOCALE_DATE_FORMATS: Record<string, string> = {
  "zh-tw": DateFormats.YYYY_M_D_CJK,
  zh: DateFormats.YYYY_M_D_CJK,
};

const LOCALE_ALIAS: Record<string, string> = {
  "es-419": "es",
};

const normalizeDayjsLocale = (language: string): string => {
  const lower = language.toLowerCase();
  return LOCALE_ALIAS[lower] ?? lower;
};

export const formatEpochToLocaleDate = (
  epochMillis?: number | null,
  language = "en",
): string => {
  if (epochMillis == null) return "";
  const num = Number(epochMillis);
  if (!Number.isFinite(num) || num < 0) return "-";

  const locale = normalizeDayjsLocale(language);
  const format = LOCALE_DATE_FORMATS[locale] ?? DateFormats.DD_MMM_YYYY;
  return dayjsLib(num).locale(locale).format(format);
};

export const formatToDateString = (
  date?: Date | string | null,
  format: string = DateFormats.YYYY_MM_DD,
): string => {
  if (!date) {
    return "";
  }
  return dayjsLib(date).format(format);
};

/**
 * Formats a date to "DD MMM YYYY, HH:mm UTC+X" (viewer's local offset),
 * e.g. "10 Feb 2025, 13:38 UTC+8".
 */
export const formatDateWithUtcOffset = (date?: Date | Dayjs): string => {
  const d = dayjsLib(date);
  const utcOffset = d
    .format("Z")
    .replace(/:00$/, "")
    .replace(/([+-])0/, "$1");
  return `${d.format("DD MMM YYYY, HH:mm")} UTC${utcOffset}`;
};

/**
 * The "Last updated" timestamp shown in last-click column tooltips. The
 * underlying data is refreshed hourly, so we surface the start of the previous
 * hour (e.g. at 15:53 this returns "14:00") — the most recent point the data is
 * guaranteed to reflect. Formatted in fixed GMT (not the viewer's local
 * offset), matching the Performance Report's "Last updated" label — see
 * `lastUpdatedFromSync` in `PerformanceReport/helpers.ts`.
 */
export const formatLastUpdatedTooltipDate = (): string =>
  formatDateWithUtcOffset(dayjsLib().startOf("hour").subtract(1, "hour"));
